<script setup lang="ts">
import { ref } from "vue";
import { useApiClient } from "@/api/client";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const fileInputRef = ref<HTMLInputElement | null>(null);
const selectedFile = ref<File | null>(null);
const isDragging = ref(false);
const uploading = ref(false);
const errorMessage = ref("");
const queued = ref(false);

const triggerFileInput = () => {
	if (!uploading.value) {
		fileInputRef.value?.click();
	}
};

const onFileSelected = (event: Event) => {
	const input = event.target as HTMLInputElement;
	if (input.files?.[0]) {
		setFile(input.files[0]);
	}
};

const onDrop = (event: DragEvent) => {
	isDragging.value = false;
	if (!uploading.value && event.dataTransfer?.files?.[0]) {
		setFile(event.dataTransfer.files[0]);
	}
};

const setFile = (file: File) => {
	if (!file.name.toLowerCase().endsWith(".xlsx")) {
		errorMessage.value = t("pqa.steeringRules.import.invalidType");
		return;
	}
	selectedFile.value = file;
	errorMessage.value = "";
	queued.value = false;
};

const uploadFile = async () => {
	if (!selectedFile.value) return;

	uploading.value = true;
	errorMessage.value = "";
	queued.value = false;

	const response = await useApiClient("pqa").importSteeringRules(
		selectedFile.value
	);

	uploading.value = false;

	if (response.isOk()) {
		queued.value = true;
		selectedFile.value = null;
		if (fileInputRef.value) {
			fileInputRef.value.value = "";
		}
	} else if (response.isErr()) {
		errorMessage.value = response.error.message;
	}
};
</script>

<template>
	<v-card class="pa-6">
		<div
			class="upload-zone rounded-lg d-flex flex-column align-center justify-center"
			:class="{ 'is-dragging': isDragging, 'is-disabled': uploading }"
			@click="triggerFileInput"
			@dragover.prevent="isDragging = true"
			@dragleave.prevent="isDragging = false"
			@drop.prevent="onDrop"
		>
			<v-icon
				size="56"
				:color="selectedFile ? 'primary' : 'medium-emphasis'"
				class="mb-3"
			>
				{{
					selectedFile ? "mdi-file-check-outline" : "mdi-cloud-upload-outline"
				}}
			</v-icon>

			<template v-if="selectedFile">
				<p class="text-subtitle-1 font-weight-medium">
					{{ selectedFile.name }}
				</p>
				<p class="text-body-2 text-medium-emphasis mt-1">
					{{ (selectedFile.size / 1024).toFixed(1) }} KB —
					<span class="text-primary">{{
						t("pqa.steeringRules.import.dropZone.changeFile")
					}}</span>
				</p>
			</template>
			<template v-else>
				<p class="text-subtitle-1 font-weight-medium">
					{{ t("pqa.steeringRules.import.dropZone.title") }}
				</p>
				<p class="text-body-2 text-medium-emphasis mt-1">
					{{ t("pqa.steeringRules.import.dropZone.subtitle") }}
				</p>
			</template>

			<input
				ref="fileInputRef"
				type="file"
				accept=".xlsx"
				class="d-none"
				@change="onFileSelected"
			/>
		</div>

		<v-progress-linear
			v-if="uploading"
			indeterminate
			color="primary"
			class="mt-4"
			rounded
		/>

		<v-alert v-if="errorMessage" type="error" variant="tonal" class="mt-4">
			{{ errorMessage }}
		</v-alert>

		<v-alert v-if="queued" type="info" variant="tonal" class="mt-4">
			{{ t("pqa.steeringRules.import.queued") }}
		</v-alert>

		<div class="d-flex ga-3 mt-5">
			<v-btn
				color="primary"
				variant="flat"
				:disabled="!selectedFile || uploading"
				:loading="uploading"
				@click="uploadFile"
			>
				{{ t("pqa.steeringRules.import.submit") }}
			</v-btn>
		</div>
	</v-card>
</template>

<style scoped lang="scss">
.upload-zone {
	border: 2px dashed rgba(var(--v-border-color), 0.5);
	min-height: 220px;
	cursor: pointer;
	transition: border-color 0.2s, background-color 0.2s;
	padding: 48px 32px;

	&:hover:not(.is-disabled) {
		border-color: rgba(var(--v-theme-primary), 0.6);
		background-color: rgba(var(--v-theme-primary), 0.03);
	}

	&.is-dragging {
		border-color: rgb(var(--v-theme-primary));
		background-color: rgba(var(--v-theme-primary), 0.06);
	}

	&.is-disabled {
		opacity: 0.6;
		cursor: default;
	}
}
</style>
